import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Compass, Save, Users, Star, Building2, Loader2 } from 'lucide-react';
import { PageLayout } from '../../../components/ui/PageLayout';
import { Switch } from '../../../components/ui/Switch';
import type { RootState } from '../../../store/redux/store/store';
import {
  getCompanyMission,
  updateCompanyMission,
  getMyStatement,
  createOrUpdateStatement,
  getTeamStatements,
  type MissionStatementView,
} from '../../../api/PreformanceManagement/MissionStatements';

/* ─── Rating Stars Component ─── */
function RatingStars({
  value,
  onChange,
  readonly = false,
}: {
  value: number;
  onChange?: (v: number) => void;
  readonly?: boolean;
}) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={readonly}
          onClick={() => onChange?.(star)}
          className={`transition-colors ${
            readonly ? 'cursor-default' : 'cursor-pointer hover:scale-110'
          } ${star <= value ? 'text-amber-400' : 'text-muted/40'}`}
        >
          <Star className="h-5 w-5" fill={star <= value ? 'currentColor' : 'none'} />
        </button>
      ))}
    </div>
  );
}

/* ─── Main Component ─── */
export default function MissionStatements() {
  const { t } = useTranslation();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const role = useSelector((s: RootState) => (s.user as any)?.role ?? '');
  const isHr = ['Hr', 'Admin', 'Chief', 'Operator'].some((r) => role.includes(r));

  // State
  const [companyMission, setCompanyMission] = useState<string>('');
  const [editingCompanyMission, setEditingCompanyMission] = useState(false);
  const [companyMissionDraft, setCompanyMissionDraft] = useState('');
  // Tracks the persisted statement (used to hydrate form + show saved timestamp)
  const [myStatement, setMyStatement] = useState<MissionStatementView | null>(null);
  const [teamStatements, setTeamStatements] = useState<MissionStatementView[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Personal statement form
  const [statementText, setStatementText] = useState('');
  const [rating, setRating] = useState(3);
  const [isShared, setIsShared] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [mission, mine, team] = await Promise.all([
        getCompanyMission().catch(() => null),
        getMyStatement().catch(() => null),
        getTeamStatements().catch(() => []),
      ]);
      setCompanyMission(mission ?? '');
      setMyStatement(mine);
      if (mine) {
        setStatementText(mine.statement);
        setRating(mine.rating);
        setIsShared(mine.isShared);
      }
      setTeamStatements(team ?? []);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSaveCompanyMission = async () => {
    setSaving(true);
    try {
      await updateCompanyMission(companyMissionDraft);
      setCompanyMission(companyMissionDraft);
      setEditingCompanyMission(false);
    } catch {
      // silent
    } finally {
      setSaving(false);
    }
  };

  const handleSavePersonal = async () => {
    if (!statementText.trim()) return;
    setSaving(true);
    try {
      const result = await createOrUpdateStatement({
        statement: statementText,
        rating,
        isShared,
      });
      setMyStatement(result);
      // Refresh team statements in case sharing changed
      const team = await getTeamStatements().catch(() => []);
      setTeamStatements(team ?? []);
    } catch {
      // silent
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <PageLayout title={t('missionStatements.title')} icon={Compass} accent="purple">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout title={t('missionStatements.title')} icon={Compass} accent="purple">
      <div className="space-y-6">
        {/* ─── Company Mission ─── */}
        <div className="rounded-xl border border-border/60 bg-card shadow-sm overflow-hidden">
          <div className="flex items-center gap-3 px-6 py-4 border-b border-border/40 bg-gradient-to-r from-purple-500/10 via-violet-500/5 to-transparent">
            <div className="flex items-center justify-center h-9 w-9 rounded-lg bg-gradient-to-br from-purple-500 to-violet-600 text-white shadow-sm">
              <Building2 className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <h2 className="text-base font-semibold text-foreground">
                {t('missionStatements.companyMission')}
              </h2>
              <p className="text-xs text-muted-foreground">
                {t('missionStatements.companyMissionDesc')}
              </p>
            </div>
            {isHr && !editingCompanyMission && (
              <button
                onClick={() => {
                  setCompanyMissionDraft(companyMission);
                  setEditingCompanyMission(true);
                }}
                className="px-3 py-1.5 text-xs font-medium rounded-lg border border-border/60 bg-card hover:bg-muted/50 text-foreground transition-colors"
              >
                {t('missionStatements.edit')}
              </button>
            )}
          </div>
          <div className="p-6">
            {editingCompanyMission ? (
              <div className="space-y-3">
                <textarea
                  value={companyMissionDraft}
                  onChange={(e) => setCompanyMissionDraft(e.target.value)}
                  rows={4}
                  className="w-full rounded-lg border border-border/60 bg-background px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-purple-500/40 resize-none"
                  placeholder={t('missionStatements.companyMissionPlaceholder')}
                />
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={() => setEditingCompanyMission(false)}
                    className="px-4 py-2 text-xs font-medium rounded-lg border border-border/60 bg-card hover:bg-muted/50 text-foreground transition-colors"
                  >
                    {t('missionStatements.cancel')}
                  </button>
                  <button
                    onClick={handleSaveCompanyMission}
                    disabled={saving}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-lg bg-purple-600 hover:bg-purple-700 text-white transition-colors disabled:opacity-50"
                  >
                    {saving ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Save className="h-3.5 w-3.5" />
                    )}
                    {t('missionStatements.save')}
                  </button>
                </div>
              </div>
            ) : companyMission ? (
              <p className="text-sm text-foreground/80 leading-relaxed whitespace-pre-wrap">
                {companyMission}
              </p>
            ) : (
              <p className="text-sm text-muted-foreground italic">
                {t('missionStatements.noCompanyMission')}
              </p>
            )}
          </div>
        </div>

        {/* ─── Personal Mission Statement ─── */}
        <div className="rounded-xl border border-border/60 bg-card shadow-sm overflow-hidden">
          <div className="flex items-center gap-3 px-6 py-4 border-b border-border/40">
            <div className="flex items-center justify-center h-9 w-9 rounded-lg bg-gradient-to-br from-teal-500 to-cyan-600 text-white shadow-sm">
              <Compass className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground">
                {t('missionStatements.myStatement')}
              </h2>
              <p className="text-xs text-muted-foreground">
                {t('missionStatements.myStatementDesc')}
              </p>
            </div>
          </div>
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                {t('missionStatements.statementLabel')}
              </label>
              <textarea
                value={statementText}
                onChange={(e) => setStatementText(e.target.value)}
                rows={4}
                className="w-full rounded-lg border border-border/60 bg-background px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-teal-500/40 resize-none"
                placeholder={t('missionStatements.statementPlaceholder')}
              />
            </div>

            <div className="flex flex-wrap items-center gap-6">
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                  {t('missionStatements.ratingLabel')}
                </label>
                <RatingStars value={rating} onChange={setRating} />
              </div>

              <div className="pt-3">
                <Switch
                  checked={isShared}
                  onCheckedChange={setIsShared}
                  label={t('missionStatements.shareWithTeam')}
                  description={t('missionStatements.shareDesc')}
                />
              </div>
            </div>

            <div className="flex items-center justify-between pt-2">
              {myStatement?.updatedOn ? (
                <span className="text-xs text-muted-foreground">
                  {t('missionStatements.lastSaved')}{' '}
                  {new Date(myStatement.updatedOn).toLocaleDateString()}
                </span>
              ) : myStatement ? (
                <span className="text-xs text-muted-foreground">
                  {t('missionStatements.lastSaved')}{' '}
                  {new Date(myStatement.createdOn).toLocaleDateString()}
                </span>
              ) : (
                <span />
              )}
              <button
                onClick={handleSavePersonal}
                disabled={saving || !statementText.trim()}
                className="inline-flex items-center gap-1.5 px-5 py-2 text-sm font-medium rounded-lg bg-teal-600 hover:bg-teal-700 text-white transition-colors disabled:opacity-50"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                {t('missionStatements.saveStatement')}
              </button>
            </div>
          </div>
        </div>

        {/* ─── Team Statements ─── */}
        <div className="rounded-xl border border-border/60 bg-card shadow-sm overflow-hidden">
          <div className="flex items-center gap-3 px-6 py-4 border-b border-border/40">
            <div className="flex items-center justify-center h-9 w-9 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-sm">
              <Users className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground">
                {t('missionStatements.teamStatements')}
              </h2>
              <p className="text-xs text-muted-foreground">
                {t('missionStatements.teamStatementsDesc')}
              </p>
            </div>
          </div>
          <div className="p-6">
            {teamStatements.length === 0 ? (
              <p className="text-sm text-muted-foreground italic text-center py-8">
                {t('missionStatements.noTeamStatements')}
              </p>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {teamStatements.map((s) => (
                  <div
                    key={s.id}
                    className="rounded-lg border border-border/40 bg-muted/20 p-4 space-y-3"
                  >
                    <div className="flex items-center gap-3">
                      {s.userImageUrl ? (
                        <img
                          src={s.userImageUrl}
                          alt={s.userName}
                          className="h-8 w-8 rounded-full object-cover"
                        />
                      ) : (
                        <div className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-white text-xs font-bold">
                          {(s.userName || '?')[0].toUpperCase()}
                        </div>
                      )}
                      <span className="text-sm font-medium text-foreground truncate">
                        {s.userName}
                      </span>
                    </div>
                    <p className="text-sm text-foreground/80 leading-relaxed line-clamp-4">
                      {s.statement}
                    </p>
                    <RatingStars value={s.rating} readonly />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </PageLayout>
  );
}
